import { useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";

type ScribePromptCollapsibleProps = {
  /** The resolved prompt from `AmbientScribeNote.prompt`, or any other long provenance text. */
  prompt: string | undefined | null;
  /**
   * Noun in the toggle: "Show {label}" / "Hide {label}". Defaults to `"prompt"`.
   *
   * The failure banner reuses this for raw provider text ("error details"), which is the same
   * shape of content — long, monospace-ish, and not something to show unasked.
   */
  label?: string;
  /**
   * Overridable because the audiology tab renders two of these at once — the failure detail inside
   * the Alert and the prompt below it — and duplicate test ids break `getByTestId`. Defaults keep
   * every pre-existing caller's ids byte-identical.
   */
  testId?: string;
  toggleTestId?: string;
};

/**
 * Collapsed-by-default disclosure for the provenance behind a generated note (SN-19067).
 *
 * Shared by the audiology summary tab and the chart-note SOAP tab, which is why it owns its own
 * open state rather than taking one — nothing outside needs to read or drive it.
 *
 * Renders nothing without content. `prompt` is projection-gated
 * (`AMBIENT_SCRIBE_NOTE_QUERY_WITH_PROMPT`), so "absent" is the normal case for any caller that
 * did not ask for it, not an error worth reporting.
 */
export default function ScribePromptCollapsible({
  prompt,
  label = "prompt",
  testId = "scribe-prompt-collapsible",
  toggleTestId = "scribe-prompt-toggle"
}: Readonly<ScribePromptCollapsibleProps>) {
  const [expanded, setExpanded] = useState(false);

  if (!prompt?.trim()) return null;

  return (
    <Box mt={2} data-testid={testId}>
      <Button
        onClick={() => setExpanded((prev) => !prev)}
        size="small"
        data-testid={toggleTestId}
        endIcon={
          <ExpandMoreIcon
            sx={{
              transition: "transform 200ms ease",
              transform: expanded ? "rotate(180deg)" : "none"
            }}
          />
        }
        sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}
      >
        {expanded ? `Hide ${label}` : `Show ${label}`}
      </Button>

      <Collapse in={expanded} unmountOnExit>
        {/*
          A prompt embeds the whole transcript, so it is routinely thousands of lines. It scrolls
          inside its own box rather than growing the dialog, and stays monospace + pre-wrap
          because whitespace is part of what makes a prompt readable.
        */}
        <Box
          sx={{
            mt: 1,
            p: 1.5,
            maxHeight: 300,
            overflowY: "auto",
            borderRadius: "6px",
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "action.hover"
          }}
        >
          <Typography
            variant="body2"
            component="pre"
            sx={{
              fontFamily: "monospace",
              fontSize: "0.75rem",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              m: 0
            }}
          >
            {prompt}
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
}
